import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "../../../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import {
  CheckCircle2,
  Circle,
  Calendar,
  Tag,
  MoreVertical,
  Edit,
  Trash2,
  Flag,
  Check,
  X,
  Lightbulb,
  Link as LinkIcon,
  Clock,
  Sparkles,
  ListTodo,
  Globe,
  Target,
  FileText,
  Play,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { UserCategory } from "../../../../api/userCategories";
import { cn } from "../../../../lib/utils";
import { useSpaces } from "../../../../context/SpaceProvider";
import { useProjectIndex } from "../../../../hooks/useProjectIndex";
import MmLiftMenuItem from "../../../mentalModel/MmLiftMenuItem";
import { ProjectChip } from "../ProjectChip";

interface ActionItemCardProps {
  item: UserTakeaway;
  isSelected: boolean;
  categories: UserCategory[];
  onToggleSelect: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onEdit: (item: UserTakeaway) => void;
  onDelete: (id: string) => void;
  onSetPriority: (id: string, priority: "high" | "medium" | "low" | null) => void;
  onAssignCategory: (id: string, categoryId: string | null) => void;
  onAssignSpace?: (id: string, spaceIds: string[]) => void;
  onNeedHelp: (item: UserTakeaway) => void;
  onAddSubtask?: (item: UserTakeaway) => void;
  onOpenEngagement?: (item: UserTakeaway) => void;
  onOpenDesignMode?: (item: UserTakeaway) => void;
  onOpenLinkedActivity?: (item: UserTakeaway) => void;
  onProgressReport?: (item: UserTakeaway) => void;
  getSourceLabel: (source: UserTakeaway["source"]) => string;
}

const ActionItemCard: React.FC<ActionItemCardProps> = ({
  item,
  isSelected,
  categories,
  onToggleSelect,
  onToggleStatus,
  onEdit,
  onDelete,
  onSetPriority,
  onAssignCategory,
  onAssignSpace,
  onNeedHelp,
  onAddSubtask,
  onOpenEngagement,
  onOpenDesignMode,
  onOpenLinkedActivity,
  onProgressReport,
  getSourceLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getPriorityBorderColor = (priority?: string) => {
    switch (priority) {
      case "high": return "border-l-red-500";
      case "medium": return "border-l-orange-500";
      case "low": return "border-l-blue-500";
      default: return "border-l-transparent";
    }
  };

  const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && item.status !== "completed";
  const isDueToday = item.dueDate && new Date(item.dueDate).toDateString() === new Date().toDateString();

  return (
    <div
      className={cn(
        "group relative bg-card border rounded-xl p-4 transition-all duration-200",
        "hover:shadow-lg hover:shadow-primary/5 hover:border-primary/20",
        item.status === "completed" && "opacity-50 bg-muted/20",
        isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        "border-l-[3px]",
        getPriorityBorderColor(item.priority)
      )}
    >
      {/* Top row: checkbox, status, menu */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(item._id)}
            className="mt-0.5"
          />
          <button
            className={`group/toggle flex items-center justify-center w-6 h-6 rounded-full transition-colors ${item.status === "completed" ? "bg-green-500 text-white" : "bg-muted hover:bg-primary/20 text-muted-foreground"}`}
            onClick={() => onToggleStatus(item._id)}
          >
            <Check className={`h-3.5 w-3.5 transition-opacity ${item.status === "completed" ? "opacity-100" : "opacity-0 group-hover/toggle:opacity-50"}`} />
          </button>
        </div>

        <div className="flex items-center gap-0.5">
          {/* Quick action buttons on hover */}
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-150 flex items-center">
            {item.status !== "completed" && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      onClick={() => onNeedHelp(item)}
                    >
                      <Lightbulb className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("action_items.need_help", "Need help?")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => onEdit(item)}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("edit", "Edit")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-50 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(item)}>
                <Edit className="h-4 w-4 mr-2" />
                {t("edit")}
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => onToggleStatus(item._id)}>
                {item.status === "completed" ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    {t("mark_incomplete")}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("mark_complete")}
                  </>
                )}
              </DropdownMenuItem>

              <MmLiftMenuItem entityId={item._id} />

              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Flag className="h-4 w-4 mr-2" />
                  {t("set_priority")}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => onSetPriority(item._id, null)}>
                      <X className="h-4 w-4 mr-2 text-muted-foreground" />
                      {t("no_priority")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onSetPriority(item._id, "high")}>
                      <Flag className="h-4 w-4 mr-2 text-red-500" />
                      {t("high")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSetPriority(item._id, "medium")}>
                      <Flag className="h-4 w-4 mr-2 text-orange-500" />
                      {t("medium")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSetPriority(item._id, "low")}>
                      <Flag className="h-4 w-4 mr-2 text-primary" />
                      {t("low")}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Tag className="h-4 w-4 mr-2" />
                  {t("assign_category")}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => onAssignCategory(item._id, null)}>
                      <X className="h-4 w-4 mr-2 text-muted-foreground" />
                      {t("no_category")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {categories.map((category) => (
                      <DropdownMenuItem
                        key={category._id}
                        onClick={() => onAssignCategory(item._id, category._id)}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full border"
                            style={{ backgroundColor: category.color }}
                          />
                          {category.name}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              {onAssignSpace && spaces && spaces.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Globe className="h-4 w-4 mr-2" />
                    {t("assign_to_space") || "Assign to Space"}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {spaces.map((space) => {
                        const isAssigned = item.spaceIds?.includes(space._id);
                        return (
                          <DropdownMenuItem
                            key={space._id}
                            onClick={() => {
                              const currentIds = item.spaceIds || [];
                              const newIds = isAssigned
                                ? currentIds.filter((id) => id !== space._id)
                                : [...currentIds, space._id];
                              onAssignSpace(item._id, newIds);
                            }}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: space.color }}
                              />
                              <span className="flex-1 truncate">
                                {space.name}
                              </span>
                              {isAssigned && (
                                <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                              )}
                            </div>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}

              {onAddSubtask && item.status !== "completed" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onAddSubtask(item)}>
                    <ListTodo className="h-4 w-4 mr-2 text-primary" />
                    {t("action_items.add_subtasks", "Add Sub-tasks")}
                  </DropdownMenuItem>
                </>
              )}

              {onProgressReport && (
                <DropdownMenuItem onClick={() => onProgressReport(item)}>
                  <FileText className="h-4 w-4 mr-2 text-teal-500" />
                  {t("progress_report", "Progress Update")}
                </DropdownMenuItem>
              )}

              {(item.source.fiProcess || item.source.fiPrescription) && (
                <>
                  <DropdownMenuSeparator />
                  {item.source.type === "engagement" && onOpenEngagement && (
                    <DropdownMenuItem onClick={() => onOpenEngagement(item)}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      {t("view_engagement")}
                    </DropdownMenuItem>
                  )}
                  {item.source.type === "sketchbook" && onOpenDesignMode && (
                    <DropdownMenuItem onClick={() => onOpenDesignMode(item)}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      {t("open_in_design_mode")}
                    </DropdownMenuItem>
                  )}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(item._id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t("delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      {item.source.type === "reflection" && item.source.question && (
        <p className="text-xs text-muted-foreground/70 italic line-clamp-1 mb-1">
          {item.source.question}
        </p>
      )}
      <div
        className={cn(
          "text-sm leading-relaxed mb-4",
          item.status === "completed" && "line-through text-muted-foreground"
        )}
      >
        {item.content}
      </div>

      {/* Meta info row */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Long-term goal indicator */}
        {item.isLongTermGoal && (
          <Badge variant="outline" className="h-5 text-[10px] font-normal px-2 border-teal-300 text-teal-600 bg-teal-50 dark:bg-teal-900/20">
            <Target className="h-2.5 w-2.5 mr-1" />
            {t("long_term_goal", "Long-term goal")}
          </Badge>
        )}

        {/* Project chip — which life project this task was planted in */}
        <ProjectChip item={item} projectById={projectById} spaces={spaces} variant="badge" />

        {/* Progress report count badge */}
        {item.progressReportCount && item.progressReportCount > 0 && onProgressReport && (
          <Badge
            variant="outline"
            className="h-5 text-[10px] font-normal px-2 border-teal-300 text-teal-600 bg-teal-50 dark:bg-teal-900/20 cursor-pointer hover:bg-teal-100"
            onClick={() => onProgressReport(item)}
          >
            <FileText className="h-2.5 w-2.5 mr-1" />
            {item.progressReportCount} {item.progressReportCount === 1 ? t("update", "update") : t("updates", "updates")}
          </Badge>
        )}

        {/* Category badge */}
        {item.category && (
          <Badge variant="outline" className="h-5 text-[10px] font-normal px-2" style={{ borderColor: item.category.color, color: item.category.color }}>
            {item.category.name}
          </Badge>
        )}

        {/* Priority badge */}
        {item.priority && (
          <Badge variant={getPriorityColor(item.priority)} className="h-5 text-[10px] font-normal px-2">
            {t(item.priority)}
          </Badge>
        )}

        {/* Due date */}
        {item.dueDate && (
          <Badge
            variant="outline"
            className={cn(
              "h-5 text-[10px] font-normal px-2",
              isOverdue && "border-red-400 text-red-600 bg-red-50 dark:bg-red-900/20",
              isDueToday && !isOverdue && "border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-900/20"
            )}
          >
            <Calendar className="h-2.5 w-2.5 mr-1" />
            {isOverdue ? t("overdue", "Overdue") : isDueToday ? t("today", "Today") : new Date(item.dueDate).toLocaleDateString()}
          </Badge>
        )}

        {/* Space badges */}
        {item.spaceIds && item.spaceIds.length > 0 && spaces && spaces.map((space) => {
          if (!item.spaceIds?.includes(space._id)) return null;
          return (
            <Badge
              key={space._id}
              variant="outline"
              className="h-5 text-[10px] font-normal px-2"
              style={{ borderColor: space.color + "50", color: space.color }}
            >
              <Globe className="w-2.5 h-2.5 mr-1" />
              {space.name}
            </Badge>
          );
        })}

        {/* Subtask progress indicator */}
        {item.subtaskProgress && (
          <Badge variant="secondary" className="h-5 text-[10px] font-normal px-2">
            <ListTodo className="h-2.5 w-2.5 mr-1" />
            {item.subtaskProgress.completed}/{item.subtaskProgress.total}
          </Badge>
        )}

        {/* Linked activity indicator - clickable to open activity */}
        {item.linkedActivity && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className={cn(
                    "h-5 text-[10px] font-normal px-2 text-purple-600 border-purple-200 bg-purple-50 dark:bg-purple-900/20",
                    onOpenLinkedActivity && "cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                  )}
                  onClick={() => onOpenLinkedActivity?.(item)}
                >
                  <Sparkles className="h-2.5 w-2.5 mr-1" />
                  {item.linkedActivity.engagementProgress ? (
                    <span>
                      {item.linkedActivity.engagementProgress.completedPanes}/{item.linkedActivity.engagementProgress.totalPanes}
                    </span>
                  ) : (
                    t("action_items.has_activity", "Activity")
                  )}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <p className="font-medium">{item.linkedActivity.prescriptionName}</p>
                  {item.linkedActivity.engagementProgress && (
                    <p className="text-muted-foreground mt-1">
                      {item.linkedActivity.engagementProgress.started
                        ? t("action_items.activity_progress", "Progress: {{completed}}/{{total}}", {
                            completed: item.linkedActivity.engagementProgress.completedPanes,
                            total: item.linkedActivity.engagementProgress.totalPanes
                          })
                        : t("action_items.activity_not_started", "Not started yet")}
                    </p>
                  )}
                  {onOpenLinkedActivity && (
                    <p className="text-muted-foreground mt-1">{t("action_items.click_to_open", "Click to open")}</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {/* PWF: Run as Activity button — shown when item has an active Activity manifestation */}
        {item.__isPwf && item.manifestations?.some(m => m.pwfType === "activity" && m.isActive) && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/activities/run?source=pwf&pwfId=${item._id}`);
                  }}
                >
                  <Play className="h-2.5 w-2.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Run as Activity</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Source info */}
      <div className="mt-3 pt-3 border-t border-dashed text-[10px] text-muted-foreground/70 flex items-center gap-1.5">
        <Clock className="h-3 w-3" />
        <span className="truncate">{getSourceLabel(item.source)}</span>
      </div>
    </div>
  );
};

export default ActionItemCard;
